export const SECTION_NAME = "PickTicket";

export const INTRO_TEXT = `A pick ticket gives instructions to
  your warehouse to pull certain items, quantities, serial numbers, lot numbers,
  etc. Pick tickets do NOT remove items from inventory.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "EST-12345",
    "date": "2019-05-08T09:13:00",
    "customer": {
      "id": 23,
      "name": "Test Customer"
    },
    "location": {
        "id": 10,
        "name": "Warehouse West"
    },
    "channel": {
      "id": 3,
      "name": "Catalog"
    },
    "priority": {
      "id": 4,
      "name": "A"
    },
    "assignedToUser": {
      "id": 38,
      "name": "Jeri Sheff"
    },
    "shippingMethod": {
      "id": 7,
      "name": "UPS Ground"
    },
    "trackingNumber": "F84FSOI2O3FJV90012S",
    "linkedTransaction": null,
    "comment": "Pricing per unit",
    "customerNotes": "lead time test",
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Cust-Acct-No",
        "value": null,
        "dataType": "Number"
      }
    ],
    "pickBy": "2019-06-17T00:00:00",
    "total": 12,
    "archived": false,
    "summaryOnly": false,
    "hasSignature": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "class": null,
        "job": {
          "id": 51,
          "name": "A62"
        },
        "workcenter": {
          "id": 10,
          "name": "Punch"
        },
        "tax": {
          "taxable": true,
          "taxCode": {
            "id": 1,
            "name": "TAX"
          },
        },
        "description": null,
        "quantity": 1,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 12.00000,
        "amount": 12.00000,
        "cost": 1.00000,
        "margin": 1100.00000,
        "listprice": 0.00000,
        "percentdiscount": 0.00000
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a pick ticket, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "Customer for this transaction.",
  },
  {
    name: "location",
    type: "reference",
    description: "Location for this transaction.",
  },
  {
    name: "channel",
    type: "reference",
    description: "Channel (e.g., Catalog, Retail Store) for this transaction.",
  },
  {
    name: "priority",
    type: "reference",
    description:
      "The degree of importance or urgency assigned to this transaction.",
  },
  {
    name: "assignedToUser",
    type: "reference",
    description: `User to whom this transaction is assigned.`,
  },
  {
    name: "shippingMethod",
    type: "reference",
    description: `Shipping method for this transaction.`,
  },
  {
    name: "trackingNumber",
    type: "string",
    description: `Shipping carrier's tracking number.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this pick ticket.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the customer.",
  },
  {
    name: "customerNotes",
    type: "string",
    description: `Field for internal notes about customer.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "pickBy",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Date by which this order should be picked.`,
  },
  {
    name: "total",
    type: "decimal",
    description: `Transaction total.`,
    readOnly: true,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `True if the summary parameter was set when this record was retrieved. False if not.`,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
  },
  {
    name: "lines",
    type: "array of line items",
    description: `The lines for the pick ticket.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the pick ticket transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity of this item for this line.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "lot",
    type: "reference",
    description: `The lot used for this line item.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `The bin used for this line item.`,
  },
  {
    name: "serials",
    type: "array of references",
    description: `Serial number references for this line item.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the pick ticket will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, comment, customer name, or location name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "location",
    type: "string",
    description: `Filters transactions according to the name of the location.`,
  },
  {
    name: "channel",
    type: "string",
    description: `The name of a channel to filter the transactions.`,
  },
  {
    name: "status",
    type: "string: open/closed",
    description: `This will filter the results by closed or open status.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/pickticket?start=101&maxresults=100&status=open&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&location=warehouse&channel=catalog&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
